/**
 * Workforce Training Schedule Page (Phase 1 of the LMS integration)
 *
 * A facilitator schedules a SEQUENCE of trainings ("series"), invites a learner
 * (defensora member) to the whole schedule or a single session, and the learner
 * ACCEPTS the schedule.
 *
 * This replaces the dead workforce "mentoring" page stubs. Backend lives under
 * /api/workforce/training-series (capability-gated server-side; LCC has it).
 */

import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Plus,
  X,
  Clock,
  MapPin,
  Users,
  UserPlus,
  ChevronRight,
  CheckCircle,
  Video,
  Trash2,
  ListChecks,
  Search,
  CheckCircle2,
  XCircle,
  MinusCircle,
  ClipboardCheck,
  UserCheck,
  Save,
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { PageHeader, PageContainer } from '@/components/weavein';
import { authFetch } from '@/lib/authFetch';
import SeriesRemindersPanel from './SeriesRemindersPanel';

// Helper to get auth token from admin dashboard localStorage
const getAuthToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

// ---- Types -------------------------------------------------------------------

interface TrainingSeriesSummary {
  series_id: number;
  series_title: string;
  sessions: number;
  starts: string | null;
  curriculum_id: number | null;
}

interface TrainingSession {
  event_id: number;
  seq: number;
  title: string;
  start_time: string | null;
  end_time: string | null;
  location_type: string | null;
  status: string | null;
  invited: number;
  accepted: number;
  my_status: string | null;
}

interface TrainingSeriesDetail {
  series_id: number;
  series_title: string;
  curriculum_id: number | null;
  sessions: TrainingSession[];
}

interface Learner {
  member_id: number;
  full_name: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

// ---- Attendance (Phase 2) ----------------------------------------------------

type AttendanceMode = 'in_person' | 'remote';

interface AttendanceLearner {
  member_id: number | null;
  user_id: number | null;
  external_email: string | null;
  name?: string | null;
  sessions_attended: number;
  sessions_total: number;
  attendance_rate: number | null;
}

interface AttendanceRollup {
  series_id: number;
  sessions_total: number;
  learners: AttendanceLearner[];
}

interface AttendanceDraft {
  attended: boolean;
  mode: AttendanceMode;
}

// Stable key for a rollup learner (member / user / email fallthrough).
const learnerKey = (l: AttendanceLearner): string => {
  if (l.member_id != null) return `m:${l.member_id}`;
  if (l.user_id != null) return `u:${l.user_id}`;
  return `e:${l.external_email ?? ''}`;
};

const learnerLabel = (l: AttendanceLearner): string => {
  if (l.name && l.name.trim()) return l.name;
  if (l.member_id != null) return `Member #${l.member_id}`;
  if (l.external_email) return l.external_email;
  if (l.user_id != null) return `User #${l.user_id}`;
  return 'Unknown learner';
};

// Identity fields to POST for a learner in an attendance record.
const learnerIdentity = (l: AttendanceLearner): Record<string, unknown> => {
  if (l.member_id != null) return { member_id: l.member_id };
  if (l.user_id != null) return { user_id: l.user_id };
  return { external_email: l.external_email };
};

// attendance_rate may arrive as a 0..1 fraction or a 0..100 percent; normalize.
const ratePct = (l: AttendanceLearner): number => {
  const r = l.attendance_rate;
  if (r == null) {
    return l.sessions_total ? Math.round((l.sessions_attended / l.sessions_total) * 100) : 0;
  }
  return r <= 1 ? Math.round(r * 100) : Math.round(r);
};

type LocationType = 'in_person' | 'virtual' | 'hybrid';

interface SessionDraft {
  title: string;
  start: string; // datetime-local value
  end: string; // datetime-local value
  description: string;
  location_type: '' | LocationType; // '' = inherit series
  meeting_link: string;
}

const LOCATION_TYPES: { value: LocationType; label: string }[] = [
  { value: 'in_person', label: 'In person' },
  { value: 'virtual', label: 'Virtual' },
  { value: 'hybrid', label: 'Hybrid' },
];

const PROVIDER_LABELS: Record<string, string> = {
  zoom: 'Zoom',
  google_meet: 'Google Meet',
  teams: 'Microsoft Teams',
  jitsi: 'Jitsi',
  webex: 'Webex',
  outlook: 'Outlook',
};
// Fallback list; the create modal replaces this with the org's configured
// providers (GET /api/meeting-providers) plus Jitsi and a manual option.
const MEETING_PROVIDERS: { value: string; label: string }[] = [
  { value: '', label: 'Manual link / None' },
  { value: 'jitsi', label: 'Jitsi' },
  { value: 'zoom', label: 'Zoom' },
];

const STATUS_COLORS: Record<string, string> = {
  scheduled: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-purple-100 text-purple-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

const RESPONSE_COLORS: Record<string, string> = {
  accepted: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-red-100 text-red-700',
  tentative: 'bg-amber-100 text-amber-700',
  invited: 'bg-paper-2 text-ink-500',
};

// ---- Helpers -----------------------------------------------------------------

const emptySession = (): SessionDraft => ({
  title: '',
  start: '',
  end: '',
  description: '',
  location_type: '',
  meeting_link: '',
});

const formatDateTime = (iso: string | null): string => {
  if (!iso) return 'TBD';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'TBD';
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatDate = (iso: string | null): string => {
  if (!iso) return 'No date set';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'No date set';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

// datetime-local (local time, no tz) -> full ISO string
const toIso = (localValue: string): string => {
  if (!localValue) return '';
  const d = new Date(localValue);
  return isNaN(d.getTime()) ? '' : d.toISOString();
};

const parseDetail = (msg: unknown, fallback: string): string => {
  const anyMsg = msg as any;
  const detail = anyMsg?.detail;
  if (Array.isArray(detail)) {
    return detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join('; ');
  }
  if (typeof detail === 'string') return detail;
  if (detail) return JSON.stringify(detail);
  return fallback;
};

// ---- Create Series Modal -----------------------------------------------------

function CreateSeriesModal({
  isOpen,
  onClose,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [seriesTitle, setSeriesTitle] = useState('');
  const [locationType, setLocationType] = useState<LocationType>('virtual');
  const [meetingProvider, setMeetingProvider] = useState('');
  const [providerOptions, setProviderOptions] = useState<{ value: string; label: string }[]>(MEETING_PROVIDERS);
  const [integrations, setIntegrations] = useState<{ zoom_connected: boolean; calendar_connected: boolean } | null>(null);

  // Which native integrations the facilitator has connected (Zoom meeting
  // auto-create; Outlook/Google calendar events).
  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(getWeaveinApiUrl('/api/workforce/training-series/meeting-integrations'));
        if (res.ok) setIntegrations(await res.json());
      } catch {
        /* non-blocking */
      }
    })();
  }, []);

  // Populate the provider dropdown from the org's configured meeting providers
  // (e.g. its Zoom account), always offering Jitsi + a manual link.
  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(getWeaveinApiUrl('/api/meeting-providers'));
        if (!res.ok) return;
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.providers || data.items || [];
        const orgOpts = list
          .filter((p: any) => p.is_enabled !== false)
          .map((p: any) => ({
            value: p.provider_type || p.type,
            label: PROVIDER_LABELS[p.provider_type || p.type] || (p.provider_type || p.type),
          }));
        const opts = [{ value: '', label: 'Manual link / None' }, ...orgOpts];
        if (!opts.some((o) => o.value === 'jitsi')) opts.push({ value: 'jitsi', label: 'Jitsi' });
        setProviderOptions(opts);
      } catch {
        /* keep fallback list */
      }
    })();
  }, []);
  const [sessions, setSessions] = useState<SessionDraft[]>([emptySession()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Recurrence generator state — MUST be declared before any early return so the
  // hook count is stable when the modal opens/closes (Rules of Hooks). Having
  // these below `if (!isOpen) return null` crashed the app on open.
  const [repeatEvery, setRepeatEvery] = useState<'day' | 'week' | 'biweek' | 'month'>('week');
  const [repeatCount, setRepeatCount] = useState(4);
  const [repeatError, setRepeatError] = useState<string | null>(null);

  if (!isOpen) return null;

  const updateSession = (idx: number, patch: Partial<SessionDraft>) => {
    setSessions((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const addSession = () => setSessions((prev) => [...prev, emptySession()]);
  const removeSession = (idx: number) =>
    setSessions((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  // Recurrence generator — expand the FIRST session into a repeating schedule
  // (daily / weekly / biweekly / monthly × N), so "schedule a series" produces
  // multiple sessions without hand-entering each one. (State declared above the
  // early return.)
  const addDays = (iso: string, days: number): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    d.setDate(d.getDate() + days);
    // back to a datetime-local value (yyyy-MM-ddThh:mm) preserving local time
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const addMonths = (iso: string, months: number): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    d.setMonth(d.getMonth() + months);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const generateRecurring = () => {
    setRepeatError(null);
    const base = sessions[0];
    if (!base?.start || !base?.end) {
      setRepeatError('Set the first session’s start and end time before generating a schedule.');
      return;
    }
    const n = Math.max(1, Math.min(52, Math.round(repeatCount)));
    const stepFor = (i: number): { start: string; end: string } => {
      if (repeatEvery === 'month') {
        return { start: addMonths(base.start, i), end: addMonths(base.end, i) };
      }
      const days = repeatEvery === 'day' ? i : repeatEvery === 'biweek' ? i * 14 : i * 7;
      return { start: addDays(base.start, days), end: addDays(base.end, days) };
    };
    const baseTitle = base.title.trim() || seriesTitle.trim() || 'Session';
    const generated: SessionDraft[] = Array.from({ length: n }, (_, i) => {
      const { start, end } = stepFor(i);
      return {
        ...base,
        title: n > 1 ? `${baseTitle} — Session ${i + 1}` : baseTitle,
        start,
        end,
      };
    });
    setSessions(generated);
  };

  const resetForm = () => {
    setSeriesTitle('');
    setLocationType('virtual');
    setMeetingProvider('');
    setSessions([emptySession()]);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validSessions = sessions.filter((s) => s.title.trim() && s.start && s.end);
    if (validSessions.length === 0) {
      setError('Add at least one session with a title, start and end time.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        series_title: seriesTitle.trim(),
        location_type: locationType,
        meeting_provider: meetingProvider || undefined,
        sessions: validSessions.map((s) => ({
          title: s.title.trim(),
          start_time: toIso(s.start),
          end_time: toIso(s.end),
          description: s.description.trim() || undefined,
          location_type: s.location_type || undefined,
          meeting_link: s.meeting_link.trim() || undefined,
        })),
      };

      const response = await authFetch(getWeaveinApiUrl('/api/workforce/training-series'), {
        method: 'POST',
        jsonBody: payload,
      });

      if (response.ok) {
        resetForm();
        onCreated();
        onClose();
      } else {
        const data = await response.json().catch(() => ({}));
        setError(parseDetail(data, 'Failed to create training series'));
      }
    } catch (err) {
      console.error('Error creating training series:', err);
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-surface flex flex-col">
      <div className="px-6 py-4 border-b border-v2border flex items-center gap-3">
        <button onClick={onClose} className="p-2 hover:bg-paper-2 rounded shrink-0" title="Back to schedule">
          <ChevronRight className="w-5 h-5 rotate-180" />
        </button>
        <h2 className="text-lg font-semibold text-ink">Schedule Training Series</h2>
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">Series Title *</label>
              <input
                type="text"
                required
                value={seriesTitle}
                onChange={(e) => setSeriesTitle(e.target.value)}
                placeholder="e.g., Defensora Onboarding Track"
                className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Location Type *</label>
                <select
                  value={locationType}
                  onChange={(e) => setLocationType(e.target.value as LocationType)}
                  className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {LOCATION_TYPES.map((lt) => (
                    <option key={lt.value} value={lt.value}>
                      {lt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Meeting Provider</label>
                <select
                  value={meetingProvider}
                  onChange={(e) => setMeetingProvider(e.target.value)}
                  className="w-full px-3 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {providerOptions.map((mp) => (
                    <option key={mp.value} value={mp.value}>
                      {mp.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-ink-400 mt-1">
                  Uses your org's configured providers. Paste a join link per session below, or leave it to add later.
                </p>
                {integrations && (
                  <p className="text-xs mt-1">
                    {meetingProvider === 'zoom' && (
                      <span className={integrations.zoom_connected ? 'text-emerald-600' : 'text-amber-600'}>
                        {integrations.zoom_connected
                          ? '✓ Zoom connected — a meeting link is created automatically.'
                          : 'Zoom not connected — links won’t auto-create (connect Zoom in Settings).'}{' '}
                      </span>
                    )}
                    <span className={integrations.calendar_connected ? 'text-emerald-600' : 'text-ink-400'}>
                      {integrations.calendar_connected
                        ? '✓ Calendar connected — sessions are added to your Outlook/Google calendar.'
                        : 'Connect Outlook/Google in Settings to auto-add these to your calendar.'}
                    </span>
                  </p>
                )}
              </div>
            </div>

            {/* Sessions */}
            <div className="bg-paper rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-ink flex items-center space-x-2">
                  <ListChecks className="w-4 h-4" />
                  <span>Sessions ({sessions.length})</span>
                </h4>
                <button
                  type="button"
                  onClick={addSession}
                  className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center space-x-1"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Session</span>
                </button>
              </div>

              {/* Recurrence generator — expand the first session into a repeating schedule */}
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-v2border bg-surface px-3 py-2 text-sm">
                <span className="text-ink-500">Repeat first session every</span>
                <select
                  value={repeatEvery}
                  onChange={(e) => setRepeatEvery(e.target.value as typeof repeatEvery)}
                  className="rounded border border-v2border px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="day">day</option>
                  <option value="week">week</option>
                  <option value="biweek">2 weeks</option>
                  <option value="month">month</option>
                </select>
                <span className="text-ink-500">for</span>
                <input
                  type="number"
                  min={1}
                  max={52}
                  value={repeatCount}
                  onChange={(e) => setRepeatCount(parseInt(e.target.value, 10) || 1)}
                  className="w-16 rounded border border-v2border px-2 py-1 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <span className="text-ink-500">sessions</span>
                <button
                  type="button"
                  onClick={generateRecurring}
                  className="ml-auto rounded-lg bg-emerald-50 px-3 py-1 font-medium text-emerald-700 hover:bg-emerald-100"
                >
                  Generate schedule
                </button>
              </div>
              {repeatError && (
                <p className="text-xs text-red-600">{repeatError}</p>
              )}

              {sessions.map((session, idx) => (
                <div key={idx} className="bg-surface border border-v2border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-ink-500 uppercase">
                      Session {idx + 1}
                    </span>
                    {sessions.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSession(idx)}
                        className="p-1 text-ink-400 hover:text-red-600 rounded"
                        title="Remove session"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-ink-500 mb-1">Title *</label>
                    <input
                      type="text"
                      value={session.title}
                      onChange={(e) => updateSession(idx, { title: e.target.value })}
                      placeholder="e.g., Module 1 - Program Overview"
                      className="w-full px-3 py-2 border border-v2border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-ink-500 mb-1">Start *</label>
                      <input
                        type="datetime-local"
                        value={session.start}
                        onChange={(e) => updateSession(idx, { start: e.target.value })}
                        className="w-full px-3 py-2 border border-v2border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-ink-500 mb-1">End *</label>
                      <input
                        type="datetime-local"
                        value={session.end}
                        onChange={(e) => updateSession(idx, { end: e.target.value })}
                        className="w-full px-3 py-2 border border-v2border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-ink-500 mb-1">
                        Location Override
                      </label>
                      <select
                        value={session.location_type}
                        onChange={(e) =>
                          updateSession(idx, { location_type: e.target.value as '' | LocationType })
                        }
                        className="w-full px-3 py-2 border border-v2border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="">Inherit from series</option>
                        {LOCATION_TYPES.map((lt) => (
                          <option key={lt.value} value={lt.value}>
                            {lt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-ink-500 mb-1">
                        Meeting Link
                      </label>
                      <input
                        type="url"
                        value={session.meeting_link}
                        onChange={(e) => updateSession(idx, { meeting_link: e.target.value })}
                        placeholder="https://..."
                        className="w-full px-3 py-2 border border-v2border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-ink-500 mb-1">
                      Description
                    </label>
                    <textarea
                      value={session.description}
                      onChange={(e) => updateSession(idx, { description: e.target.value })}
                      rows={2}
                      placeholder="Optional notes for this session..."
                      className="w-full px-3 py-2 border border-v2border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex space-x-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-v2border rounded-lg hover:bg-paper font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50"
              >
                {submitting ? 'Scheduling...' : 'Schedule Series'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ---- Invite Learner Modal ----------------------------------------------------

interface CohortOption {
  id: number;
  name: string;
  accepted: number;
  selected: number;
}

function InviteLearnerModal({
  isOpen,
  onClose,
  seriesTitle,
  learners,
  learnersLoading,
  onInvite,
  onInviteCohort,
  inviting,
  error,
}: {
  isOpen: boolean;
  onClose: () => void;
  seriesTitle: string;
  learners: Learner[];
  learnersLoading: boolean;
  onInvite: (learner: Learner, channels: string[]) => void;
  onInviteCohort: (cohortId: number, cohortName: string, channels: string[]) => void;
  inviting: boolean;
  error: string | null;
}) {
  const [query, setQuery] = useState('');
  const [channels, setChannels] = useState<string[]>(['email', 'portal']);
  const [mode, setMode] = useState<'individual' | 'cohort'>('individual');
  const [cohorts, setCohorts] = useState<CohortOption[]>([]);
  const [cohortsLoading, setCohortsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || mode !== 'cohort') return;
    setCohortsLoading(true);
    authFetch(getWeaveinApiUrl('/api/workforce/cohorts'))
      .then((res) => (res.ok ? res.json() : { cohorts: [] }))
      .then((data) => setCohorts(data.cohorts || []))
      .catch(() => setCohorts([]))
      .finally(() => setCohortsLoading(false));
  }, [isOpen, mode]);

  if (!isOpen) return null;

  const toggle = (ch: string) =>
    setChannels((cs) => (cs.includes(ch) ? cs.filter((c) => c !== ch) : [...cs, ch]));

  const filtered = learners.filter((l) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (l.full_name || '').toLowerCase().includes(q) ||
      (l.email || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative bg-surface rounded-xl shadow-xl w-full max-w-md p-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-semibold text-ink">Invite to training</h2>
            <button onClick={onClose} className="p-2 hover:bg-paper-2 rounded">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-ink-500 mb-3">
            Invite to all sessions of <span className="font-medium">{seriesTitle}</span>
          </p>

          {/* Individual vs accepted-cohort */}
          <div className="flex items-center gap-1 mb-4 border border-v2border rounded-lg p-0.5 bg-paper-2">
            {(
              [
                ['individual', 'Individual learner'],
                ['cohort', 'Accepted cohort'],
              ] as ['individual' | 'cohort', string][]
            ).map(([k, label]) => (
              <button
                key={k}
                onClick={() => setMode(k)}
                className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium ${
                  mode === k ? 'bg-surface text-ink shadow-sm' : 'text-ink-500'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="mb-4">
            <p className="text-xs font-medium text-ink-500 mb-1.5">Notify via</p>
            <div className="flex items-center gap-2">
              {[
                { key: 'email', label: 'Email' },
                { key: 'sms', label: 'Text (SMS)' },
                { key: 'portal', label: 'Member portal' },
              ].map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => toggle(c.key)}
                  className={`px-2.5 py-1 rounded-full border text-xs ${
                    channels.includes(c.key)
                      ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                      : 'border-v2border text-ink-500 hover:bg-paper-2'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-ink-400 mt-1">
              {mode === 'individual'
                ? 'Pick a learner below to invite & notify. Members who opted out of a channel are skipped.'
                : 'Invites every member who ACCEPTED the program invite for that cohort. Manage acceptance under Cohorts.'}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          {mode === 'individual' ? (
            <>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search learners..."
                  className="w-full pl-10 pr-4 py-2 border border-v2border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 border border-v2border-muted rounded-lg">
                {learnersLoading ? (
                  <div className="p-6 text-center text-ink-500 text-sm">Loading learners...</div>
                ) : filtered.length === 0 ? (
                  <div className="p-6 text-center text-ink-500 text-sm">No learners found</div>
                ) : (
                  filtered.map((learner) => (
                    <button
                      key={learner.member_id}
                      onClick={() => onInvite(learner, channels)}
                      disabled={inviting || channels.length === 0}
                      className="w-full flex items-center justify-between p-3 hover:bg-emerald-50 text-left disabled:opacity-50"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-emerald-600 font-medium text-sm">
                            {(learner.full_name || '?').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-ink truncate">
                            {learner.full_name || `Member ${learner.member_id}`}
                          </p>
                          <p className="text-sm text-ink-500 truncate">
                            {learner.email || 'No email on file'}
                          </p>
                        </div>
                      </div>
                      <UserPlus className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    </button>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 border border-v2border-muted rounded-lg">
              {cohortsLoading ? (
                <div className="p-6 text-center text-ink-500 text-sm">Loading cohorts...</div>
              ) : cohorts.length === 0 ? (
                <div className="p-6 text-center text-ink-500 text-sm">No cohorts yet</div>
              ) : (
                cohorts.map((c) => {
                  const disabled = inviting || channels.length === 0 || (c.accepted || 0) === 0;
                  return (
                    <button
                      key={c.id}
                      onClick={() => onInviteCohort(c.id, c.name, channels)}
                      disabled={disabled}
                      className="w-full flex items-center justify-between p-3 hover:bg-emerald-50 text-left disabled:opacity-50"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-ink truncate">{c.name}</p>
                        <p className="text-sm text-ink-500 truncate">
                          {c.accepted || 0} accepted · {c.selected || 0} selected
                        </p>
                      </div>
                      {(c.accepted || 0) === 0 ? (
                        <span className="text-[11px] text-ink-400 flex-shrink-0">no accepted members</span>
                      ) : (
                        <UserPlus className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Main Page ---------------------------------------------------------------

export default function TrainingSchedulePage() {
  const [series, setSeries] = useState<TrainingSeriesSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const [selectedSeries, setSelectedSeries] = useState<TrainingSeriesSummary | null>(null);
  const [detail, setDetail] = useState<TrainingSeriesDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [learners, setLearners] = useState<Learner[]>([]);
  const [learnersLoading, setLearnersLoading] = useState(false);

  const [showInvite, setShowInvite] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteMsg, setInviteMsg] = useState<string | null>(null);

  // Learner-response (demo of acceptance) state
  const [respondLearnerId, setRespondLearnerId] = useState<string>('');
  const [responding, setResponding] = useState(false);
  const [respondMsg, setRespondMsg] = useState<string | null>(null);

  // Attendance (Phase 2) state
  const [rollup, setRollup] = useState<AttendanceRollup | null>(null);
  const [rollupLoading, setRollupLoading] = useState(false);
  const [expandedSession, setExpandedSession] = useState<number | null>(null);
  const [attendanceDraft, setAttendanceDraft] = useState<Record<string, AttendanceDraft>>({});
  const [savingSession, setSavingSession] = useState<number | null>(null);
  const [attendanceMsg, setAttendanceMsg] = useState<Record<number, string>>({});

  useEffect(() => {
    fetchSeries();
  }, []);

  const fetchSeries = async () => {
    const token = getAuthToken();
    if (!token) {
      console.warn('No auth token found for training schedule page');
      setLoading(false);
      return;
    }
    try {
      const res = await authFetch(getWeaveinApiUrl('/api/workforce/training-series'));
      if (res.ok) {
        const data = await res.json();
        setSeries(data.series || []);
      }
    } catch (err) {
      console.error('Error fetching training series:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLearners = async () => {
    if (learners.length > 0) return; // cache
    setLearnersLoading(true);
    try {
      // The org's member ROSTER (member_organizations-scoped), not the
      // workforce-candidate pipeline — /unified-candidates filters to
      // member_type workforce_candidate/dual_role and returned nobody for LCC
      // (ordinary defensora members), leaving the invite picker empty.
      const res = await authFetch(
        getWeaveinApiUrl('/api/workforce/training-series/eligible-learners?limit=500')
      );
      if (res.ok) {
        const data = await res.json();
        const mapped: Learner[] = (data.learners || []).map((c: any) => ({
          member_id: c.member_id,
          full_name:
            c.full_name ||
            [c.first_name, c.last_name].filter(Boolean).join(' ') ||
            `Member ${c.member_id}`,
          first_name: c.first_name,
          last_name: c.last_name,
          email: c.email,
        }));
        setLearners(mapped);
      }
    } catch (err) {
      console.error('Error fetching learners:', err);
    } finally {
      setLearnersLoading(false);
    }
  };

  const openSeriesDetail = async (s: TrainingSeriesSummary) => {
    setSelectedSeries(s);
    setDetail(null);
    setRespondLearnerId('');
    setRespondMsg(null);
    setRollup(null);
    setExpandedSession(null);
    setAttendanceDraft({});
    setAttendanceMsg({});
    setDetailLoading(true);
    fetchLearners();
    fetchRollup(s.series_id);
    try {
      const res = await authFetch(
        getWeaveinApiUrl(`/api/workforce/training-series/${s.series_id}`)
      );
      if (res.ok) {
        const data = await res.json();
        setDetail(data);
      }
    } catch (err) {
      console.error('Error fetching series detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchRollup = async (seriesId: number) => {
    setRollupLoading(true);
    try {
      const res = await authFetch(
        getWeaveinApiUrl(`/api/workforce/training-series/${seriesId}/attendance`)
      );
      if (res.ok) {
        setRollup(await res.json());
      }
    } catch (err) {
      console.error('Error fetching attendance rollup:', err);
    } finally {
      setRollupLoading(false);
    }
  };

  const getDraft = (eventId: number, l: AttendanceLearner): AttendanceDraft =>
    attendanceDraft[`${eventId}::${learnerKey(l)}`] ?? { attended: false, mode: 'in_person' };

  const setDraft = (eventId: number, l: AttendanceLearner, patch: Partial<AttendanceDraft>) => {
    const key = `${eventId}::${learnerKey(l)}`;
    setAttendanceDraft((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? { attended: false, mode: 'in_person' }), ...patch },
    }));
  };

  const saveAttendance = async (eventId: number) => {
    if (!rollup) return;
    setSavingSession(eventId);
    setAttendanceMsg((prev) => ({ ...prev, [eventId]: '' }));
    try {
      const records = rollup.learners.map((l) => {
        const d = getDraft(eventId, l);
        return { ...learnerIdentity(l), attended: d.attended, attendance_mode: d.mode };
      });
      const res = await authFetch(
        getWeaveinApiUrl(
          `/api/workforce/training-series/sessions/${eventId}/attendance`
        ),
        { method: 'POST', jsonBody: { records } }
      );
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        setAttendanceMsg((prev) => ({
          ...prev,
          [eventId]: `Saved · ${data.attended_count ?? 0} attended of ${data.marked ?? records.length} marked.`,
        }));
        await fetchRollup(rollup.series_id);
        await refreshDetail();
      } else {
        const data = await res.json().catch(() => ({}));
        setAttendanceMsg((prev) => ({
          ...prev,
          [eventId]: parseDetail(data, 'Failed to save attendance'),
        }));
      }
    } catch (err) {
      console.error('Error saving attendance:', err);
      setAttendanceMsg((prev) => ({ ...prev, [eventId]: 'Network error. Please try again.' }));
    } finally {
      setSavingSession(null);
    }
  };

  const refreshDetail = async () => {
    if (!selectedSeries) return;
    try {
      const res = await authFetch(
        getWeaveinApiUrl(`/api/workforce/training-series/${selectedSeries.series_id}`)
      );
      if (res.ok) {
        setDetail(await res.json());
      }
    } catch (err) {
      console.error('Error refreshing series detail:', err);
    }
  };

  const handleInvite = async (learner: Learner, channels: string[]) => {
    if (!selectedSeries) return;
    setInviting(true);
    setInviteError(null);
    try {
      const res = await authFetch(
        getWeaveinApiUrl(
          `/api/workforce/training-series/${selectedSeries.series_id}/invite`
        ),
        {
          method: 'POST',
          jsonBody: { member_id: learner.member_id, attendee_role: 'learner', channels },
        }
      );
      if (res.ok) {
        const data = await res.json().catch(() => ({} as any));
        setShowInvite(false);
        const d = data?.delivery || {};
        const sent = Object.entries(d)
          .map(([k, v]) => `${k}: ${v}`)
          .join(', ');
        setInviteMsg(
          `Invited ${learner.full_name || 'learner'}${sent ? ` — notified (${sent})` : ''}.`
        );
        // default the response picker to the just-invited learner for the demo
        setRespondLearnerId(String(learner.member_id));
        await refreshDetail();
      } else {
        const data = await res.json().catch(() => ({}));
        setInviteError(parseDetail(data, 'Failed to invite learner'));
      }
    } catch (err) {
      console.error('Error inviting learner:', err);
      setInviteError('Network error. Please try again.');
    } finally {
      setInviting(false);
    }
  };

  // Invite an ACCEPTED cohort (Gap 4) — seats every accepted member of the
  // cohort into this series in one action, instead of picking individuals.
  const handleInviteCohort = async (cohortId: number, cohortName: string, channels: string[]) => {
    if (!selectedSeries) return;
    setInviting(true);
    setInviteError(null);
    try {
      const res = await authFetch(getWeaveinApiUrl(`/api/workforce/cohorts/${cohortId}/invite-to-series`), {
        method: 'POST',
        jsonBody: {
          series_id: String(selectedSeries.series_id),
          channels,
          member_statuses: ['accepted'],
          set_status: null,
        },
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}) as any);
        setShowInvite(false);
        setInviteMsg(
          `Invited ${data.invited_members ?? 0} accepted member(s) from ${cohortName}` +
            `${data.notified ? ` — notified ${data.notified}` : ''}.`,
        );
        await refreshDetail();
      } else {
        const data = await res.json().catch(() => ({}));
        setInviteError(parseDetail(data, 'Failed to invite cohort'));
      }
    } catch (err) {
      console.error('Error inviting cohort:', err);
      setInviteError('Network error. Please try again.');
    } finally {
      setInviting(false);
    }
  };

  const handleRespond = async (
    responseStatus: 'accepted' | 'declined' | 'tentative'
  ) => {
    if (!selectedSeries || !respondLearnerId) return;
    setResponding(true);
    setRespondMsg(null);
    try {
      const res = await authFetch(
        getWeaveinApiUrl(
          `/api/workforce/training-series/${selectedSeries.series_id}/respond`
        ),
        {
          method: 'POST',
          jsonBody: {
            member_id: parseInt(respondLearnerId, 10),
            response_status: responseStatus,
          },
        }
      );
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        setRespondMsg(
          `Response recorded (${responseStatus}) for ${data.sessions_updated ?? 0} session(s).`
        );
        await refreshDetail();
        fetchSeries();
      } else {
        const data = await res.json().catch(() => ({}));
        setRespondMsg(parseDetail(data, 'Failed to record response'));
      }
    } catch (err) {
      console.error('Error recording response:', err);
      setRespondMsg('Network error. Please try again.');
    } finally {
      setResponding(false);
    }
  };

  const closeDetail = () => {
    setInviteMsg(null);
    setSelectedSeries(null);
    setDetail(null);
    setRollup(null);
    setExpandedSession(null);
    setAttendanceDraft({});
    setAttendanceMsg({});
  };

  const totalSessions = series.reduce((sum, s) => sum + (s.sessions || 0), 0);

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Workforce · Training"
        title="Training Schedule"
        description="Schedule training sequences, invite learners, and track acceptances"
        actions={
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            <span>Schedule Series</span>
          </button>
        }
      />
      <PageContainer>
      <div className="space-y-6">

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 max-w-md">
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Calendar className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">Training Series</p>
                <p className="font-display text-2xl font-semibold text-ink">{series.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-surface rounded-xl p-4 border border-v2border">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ListChecks className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-ink-500">Total Sessions</p>
                <p className="font-display text-2xl font-semibold text-ink">{totalSessions}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Series List */}
        {loading ? (
          <div className="py-12 text-center text-ink-500">Loading training series...</div>
        ) : series.length === 0 ? (
          <div className="py-16 text-center bg-surface rounded-xl border border-v2border">
            <Calendar className="w-12 h-12 text-ink-300 mx-auto mb-3" />
            <p className="text-ink-500 font-medium">No training series scheduled yet</p>
            <p className="text-ink-500 text-sm mt-1">
              Create a sequence of sessions and invite learners to get started.
            </p>
            <button
              onClick={() => setShowCreate(true)}
              className="text-emerald-600 hover:text-emerald-700 font-medium mt-3"
            >
              Schedule your first series
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {series.map((s) => (
              <button
                key={s.series_id}
                onClick={() => openSeriesDetail(s)}
                className="text-left bg-surface rounded-xl border border-v2border hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <Calendar className="w-6 h-6 text-emerald-600" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-ink-400" />
                  </div>
                  <h3 className="font-semibold text-ink mb-2">{s.series_title}</h3>
                  <div className="flex items-center space-x-4 text-sm text-ink-500">
                    <span className="flex items-center">
                      <ListChecks className="w-4 h-4 mr-1" />
                      {s.sessions} session{s.sessions === 1 ? '' : 's'}
                    </span>
                    <span className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {formatDate(s.starts)}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Series Detail — full-screen (fills the area beside the app sidebar) */}
        {selectedSeries && (
          <div className="fixed inset-0 z-50 bg-surface flex flex-col">
              <div className="h-full flex flex-col max-w-6xl w-full mx-auto">
                {/* Header */}
                <div className="px-6 py-4 border-b border-v2border flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <button onClick={closeDetail} className="p-2 hover:bg-paper-2 rounded shrink-0" title="Back to schedule">
                      <ChevronRight className="w-5 h-5 rotate-180" />
                    </button>
                    <div className="min-w-0">
                      <h2 className="text-lg font-semibold text-ink truncate">
                        {selectedSeries.series_title}
                      </h2>
                      <p className="text-sm text-ink-500">
                        {selectedSeries.sessions} session
                        {selectedSeries.sessions === 1 ? '' : 's'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setInviteError(null);
                        setShowInvite(true);
                        fetchLearners();
                      }}
                      className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm flex items-center space-x-1"
                    >
                      <UserPlus className="w-4 h-4" />
                      <span>Invite Learner</span>
                    </button>
                    <button onClick={closeDetail} className="p-2 hover:bg-paper-2 rounded">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {inviteMsg && (
                    <div className="flex items-center justify-between gap-3 p-3 bg-emerald-50 text-emerald-800 rounded-lg text-sm">
                      <span>{inviteMsg}</span>
                      <button onClick={() => setInviteMsg(null)} className="text-emerald-700 hover:text-emerald-900">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  {/* Sessions */}
                  <div>
                    <h3 className="font-semibold text-ink mb-3">Sessions</h3>
                    {detailLoading ? (
                      <div className="py-8 text-center text-ink-500">Loading sessions...</div>
                    ) : !detail || detail.sessions.length === 0 ? (
                      <div className="py-8 text-center bg-paper rounded-xl">
                        <ListChecks className="w-10 h-10 text-ink-400 mx-auto mb-2" />
                        <p className="text-ink-500">No sessions in this series</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {detail.sessions.map((session) => {
                          const expanded = expandedSession === session.event_id;
                          return (
                          <div
                            key={session.event_id}
                            className="bg-surface border border-v2border rounded-lg overflow-hidden"
                          >
                            <div className="flex items-start justify-between p-4">
                            <div className="flex items-start space-x-4">
                              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-semibold flex-shrink-0">
                                {session.seq}
                              </div>
                              <div>
                                <p className="font-medium text-ink">{session.title}</p>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-500 mt-1">
                                  <span className="flex items-center">
                                    <Clock className="w-3 h-3 mr-1" />
                                    {formatDateTime(session.start_time)}
                                  </span>
                                  {session.location_type && (
                                    <span className="flex items-center">
                                      {session.location_type === 'virtual' ? (
                                        <Video className="w-3 h-3 mr-1" />
                                      ) : (
                                        <MapPin className="w-3 h-3 mr-1" />
                                      )}
                                      {session.location_type.replace('_', ' ')}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center space-x-3 mt-2 text-xs">
                                  <span className="flex items-center text-ink-500">
                                    <Users className="w-3 h-3 mr-1" />
                                    {session.invited} invited
                                  </span>
                                  <span className="flex items-center text-emerald-600">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    {session.accepted} accepted
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col items-end space-y-1">
                              {session.status && (
                                <span
                                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    STATUS_COLORS[session.status] || 'bg-paper-2 text-ink-500'
                                  }`}
                                >
                                  {session.status}
                                </span>
                              )}
                              {session.my_status && (
                                <span
                                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    RESPONSE_COLORS[session.my_status] || 'bg-paper-2 text-ink-500'
                                  }`}
                                >
                                  {session.my_status}
                                </span>
                              )}
                            </div>
                            </div>

                            {/* Attendance expander */}
                            <div className="px-4 pb-3">
                              <button
                                onClick={() =>
                                  setExpandedSession(expanded ? null : session.event_id)
                                }
                                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center"
                              >
                                <ClipboardCheck className="w-4 h-4 mr-1" />
                                {expanded ? 'Hide attendance' : 'Take attendance'}
                              </button>
                            </div>

                            {/* Attendance roster */}
                            {expanded && (
                              <div className="border-t border-v2border-muted bg-paper p-4 space-y-3">
                                {rollupLoading ? (
                                  <div className="py-4 text-center text-ink-500 text-sm">
                                    Loading roster...
                                  </div>
                                ) : !rollup || rollup.learners.length === 0 ? (
                                  <div className="py-4 text-center text-ink-500 text-sm">
                                    No learners invited yet. Invite learners to take attendance.
                                  </div>
                                ) : (
                                  <>
                                    <div className="space-y-2">
                                      {rollup.learners.map((l) => {
                                        const d = getDraft(session.event_id, l);
                                        return (
                                          <div
                                            key={learnerKey(l)}
                                            className="flex items-center justify-between bg-surface rounded-lg border border-v2border px-3 py-2"
                                          >
                                            <label className="flex items-center space-x-2 min-w-0 cursor-pointer">
                                              <input
                                                type="checkbox"
                                                checked={d.attended}
                                                onChange={(e) =>
                                                  setDraft(session.event_id, l, {
                                                    attended: e.target.checked,
                                                  })
                                                }
                                                className="w-4 h-4 rounded border-v2border text-emerald-600 focus:ring-emerald-500"
                                              />
                                              <span className="text-sm text-ink truncate">
                                                {learnerLabel(l)}
                                              </span>
                                            </label>
                                            <div className="flex items-center space-x-1 flex-shrink-0">
                                              {(['in_person', 'remote'] as AttendanceMode[]).map(
                                                (m) => {
                                                  const active = d.mode === m;
                                                  return (
                                                    <button
                                                      key={m}
                                                      type="button"
                                                      disabled={!d.attended}
                                                      onClick={() =>
                                                        setDraft(session.event_id, l, { mode: m })
                                                      }
                                                      className={`flex items-center px-2 py-1 rounded-lg text-xs font-medium border transition-colors disabled:opacity-40 ${
                                                        active
                                                          ? 'bg-emerald-600 text-white border-emerald-600'
                                                          : 'bg-surface text-ink-500 border-v2border hover:bg-paper'
                                                      }`}
                                                    >
                                                      {m === 'in_person' ? (
                                                        <MapPin className="w-3 h-3 mr-1" />
                                                      ) : (
                                                        <Video className="w-3 h-3 mr-1" />
                                                      )}
                                                      {m === 'in_person' ? 'In-person' : 'Remote'}
                                                    </button>
                                                  );
                                                }
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                    <div className="flex items-center justify-between gap-3">
                                      {attendanceMsg[session.event_id] ? (
                                        <p className="text-xs text-ink-500 min-w-0 truncate">
                                          {attendanceMsg[session.event_id]}
                                        </p>
                                      ) : (
                                        <span />
                                      )}
                                      <button
                                        onClick={() => saveAttendance(session.event_id)}
                                        disabled={savingSession === session.event_id}
                                        className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium disabled:opacity-50 flex items-center flex-shrink-0"
                                      >
                                        <Save className="w-4 h-4 mr-1" />
                                        {savingSession === session.event_id
                                          ? 'Saving...'
                                          : 'Save attendance'}
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Attendance Summary */}
                  <div>
                    <h3 className="font-semibold text-ink mb-3 flex items-center">
                      <UserCheck className="w-4 h-4 mr-2 text-emerald-600" />
                      Attendance Summary
                    </h3>
                    {rollupLoading ? (
                      <div className="py-8 text-center text-ink-500">Loading attendance...</div>
                    ) : !rollup || rollup.learners.length === 0 ? (
                      <div className="py-8 text-center bg-paper rounded-xl">
                        <ClipboardCheck className="w-10 h-10 text-ink-400 mx-auto mb-2" />
                        <p className="text-ink-500">No attendance recorded yet</p>
                        <p className="text-ink-500 text-sm mt-1">
                          Invite learners and mark who attended each session.
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-hidden border border-v2border rounded-lg">
                        <table className="w-full text-sm">
                          <thead className="bg-paper text-ink-500">
                            <tr>
                              <th className="text-left font-medium px-3 py-2">Learner</th>
                              <th className="text-center font-medium px-3 py-2">Attended</th>
                              <th className="text-right font-medium px-3 py-2">Rate</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {rollup.learners.map((l) => {
                              const pct = ratePct(l);
                              const badge =
                                pct >= 80
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : pct >= 50
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-red-100 text-red-700';
                              return (
                                <tr key={learnerKey(l)}>
                                  <td className="px-3 py-2 text-ink truncate max-w-[220px]">
                                    {learnerLabel(l)}
                                  </td>
                                  <td className="px-3 py-2 text-center text-ink-500">
                                    {l.sessions_attended}/{l.sessions_total || rollup.sessions_total}
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    <span
                                      className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${badge}`}
                                    >
                                      {pct}%
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Class reminders (email/SMS/both) — configured per series,
                      delivered server-side via the existing staff→member paths */}
                  <div className="bg-paper rounded-xl p-4">
                    <SeriesRemindersPanel seriesId={selectedSeries.series_id} />
                  </div>

                  {/* Record acceptance — in-person mode. App-user learners can
                      also self-accept via their calendar invite; this records
                      acceptance on a learner's behalf (e.g. verbal/in-person). */}
                  <div className="bg-paper rounded-xl p-4">
                    <h3 className="font-semibold text-ink mb-1 flex items-center">
                      <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" />
                      Record Acceptance
                    </h3>
                    <p className="text-sm text-ink-500 mb-3">
                      Record an invited learner's acceptance of the schedule in person.
                      Learners who use the app can also accept the invite themselves from
                      their calendar.
                    </p>
                    <div className="space-y-3">
                      <select
                        value={respondLearnerId}
                        onChange={(e) => setRespondLearnerId(e.target.value)}
                        className="w-full px-3 py-2 border border-v2border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-surface"
                      >
                        <option value="">Select an invited learner...</option>
                        {(rollup?.learners ?? [])
                          .filter((l) => l.member_id != null)
                          .map((l) => (
                            <option key={learnerKey(l)} value={String(l.member_id)}>
                              {learnerLabel(l)}
                            </option>
                          ))}
                      </select>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleRespond('accepted')}
                          disabled={!respondLearnerId || responding}
                          className="flex-1 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium disabled:opacity-50 flex items-center justify-center"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Accept
                        </button>
                        <button
                          onClick={() => handleRespond('tentative')}
                          disabled={!respondLearnerId || responding}
                          className="flex-1 px-3 py-2 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 text-sm font-medium disabled:opacity-50 flex items-center justify-center"
                        >
                          <MinusCircle className="w-4 h-4 mr-1" />
                          Tentative
                        </button>
                        <button
                          onClick={() => handleRespond('declined')}
                          disabled={!respondLearnerId || responding}
                          className="flex-1 px-3 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 text-sm font-medium disabled:opacity-50 flex items-center justify-center"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Decline
                        </button>
                      </div>
                      {respondMsg && (
                        <p className="text-sm text-ink-500 bg-surface rounded-lg p-2 border border-v2border-muted">
                          {respondMsg}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
        )}

        {/* Create Series Modal */}
        <CreateSeriesModal
          isOpen={showCreate}
          onClose={() => setShowCreate(false)}
          onCreated={fetchSeries}
        />

        {/* Invite Learner Modal */}
        {selectedSeries && (
          <InviteLearnerModal
            isOpen={showInvite}
            onClose={() => setShowInvite(false)}
            seriesTitle={selectedSeries.series_title}
            learners={learners}
            learnersLoading={learnersLoading}
            onInvite={handleInvite}
            onInviteCohort={handleInviteCohort}
            inviting={inviting}
            error={inviteError}
          />
        )}
      </div>
      </PageContainer>
    </div>
  );
}
